import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { AVATAR_COLORS, profileSchema, type AvatarColor, type ProfileInput } from '@auditwire/contracts';
import { Button, Field, Input, Textarea } from '@/shared/ui';
import { cn } from '@/shared/lib/utils/cn';
import { toMessage } from '@/shared/lib/repository/errors';
import { useUpdateProfile } from '../api/useProfile';
import type { Profile } from '../model/types';
import { ProfilePictureField } from './ProfilePictureField';

function toAvatarColor(value: string): AvatarColor {
  return (AVATAR_COLORS as readonly string[]).includes(value) ? (value as AvatarColor) : AVATAR_COLORS[0];
}

export function ProfileForm({ profile }: { profile: Profile }) {
  const updateProfile = useUpdateProfile();
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ProfileInput>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: profile.name,
      bio: profile.bio,
      avatarColor: toAvatarColor(profile.avatarColor),
      website: profile.website,
      twitter: profile.twitter,
      linkedin: profile.linkedin,
    },
  });

  const selectedColor = watch('avatarColor');
  const bioLength = (watch('bio') ?? '').length;

  const onSubmit = handleSubmit(async (values) => {
    try {
      await updateProfile.mutateAsync(values);
      toast.success('Profile updated');
    } catch (err) {
      toast.error(toMessage(err, 'Could not save your profile'));
    }
  });

  return (
    <form onSubmit={onSubmit} className="space-y-5" noValidate>
      <ProfilePictureField profile={profile} />

      <Field label="Avatar colour">
        <div className="flex flex-wrap gap-2">
          {AVATAR_COLORS.map((color) => (
            <button
              key={color}
              type="button"
              aria-label={`Use ${color.replace('bg-', '').replace('-500', '')} avatar`}
              aria-pressed={selectedColor === color}
              onClick={() => setValue('avatarColor', color, { shouldDirty: true })}
              className={cn(
                'h-8 w-8 rounded-full ring-2 ring-offset-2 ring-offset-background transition',
                color,
                selectedColor === color ? 'ring-foreground' : 'ring-transparent hover:ring-border',
              )}
            />
          ))}
        </div>
      </Field>

      <Field label="Name" htmlFor="profile-name" error={errors.name?.message}>
        <Input id="profile-name" autoComplete="name" {...register('name')} />
      </Field>

      <Field label="Bio" htmlFor="profile-bio" hint={`${bioLength}/160`} error={errors.bio?.message}>
        <Textarea id="profile-bio" rows={3} maxLength={160} {...register('bio')} />
      </Field>

      <Field label="Website" htmlFor="profile-website" error={errors.website?.message}>
        <Input id="profile-website" type="url" placeholder="https://…" {...register('website')} />
      </Field>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Twitter" htmlFor="profile-twitter" error={errors.twitter?.message}>
          <Input id="profile-twitter" placeholder="@handle" {...register('twitter')} />
        </Field>
        <Field label="LinkedIn" htmlFor="profile-linkedin" error={errors.linkedin?.message}>
          <Input id="profile-linkedin" placeholder="in/username" {...register('linkedin')} />
        </Field>
      </div>

      <Button type="submit" disabled={updateProfile.isPending}>
        {updateProfile.isPending ? 'Saving…' : 'Save changes'}
      </Button>
    </form>
  );
}
